import { c as _c } from "react/compiler-runtime";
import { Slot } from "@radix-ui/react-slot";
import type { FC, ReactNode } from "react";
import { Button } from "@/components/button";
import type { ComponentPropsWithoutChildren } from "@/lib/component-props";
import { cn } from "@/lib/utils";
export interface HeaderProps extends ComponentPropsWithoutChildren<"header"> {
  children?: ReactNode;
}
export const Header: FC<HeaderProps> = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("sticky top-0 z-50 flex items-center justify-between gap-3 border-b-[3px] border-[var(--fg-primary)] bg-[var(--bg-primary)] px-3 py-3 sm:px-4 sm:py-5 lg:px-12", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <header className={t1} {...props}>{children}</header>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export interface HeaderLogoProps extends ComponentPropsWithoutChildren<"a"> {
  children?: ReactNode;
  asChild?: boolean;
}
export const HeaderLogo: FC<HeaderLogoProps> = t0 => {
  const $ = _c(14);
  let asChild;
  let children;
  let className;
  let href;
  let props;
  if ($[0] !== t0) {
    ({
      asChild,
      className,
      children,
      href,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asChild;
    $[2] = children;
    $[3] = className;
    $[4] = href;
    $[5] = props;
  } else {
    asChild = $[1];
    children = $[2];
    className = $[3];
    href = $[4];
    props = $[5];
  }
  const Comp = asChild ? Slot : href ? "a" : "span";
  const t1 = asChild || !href ? undefined : href;
  let t2;
  if ($[6] !== className) {
    t2 = cn("font-[family-name:var(--font-anton)] text-[1.65rem] leading-none tracking-tight text-[var(--fg-primary)] focus-visible:focus-ring sm:text-[2rem]", className);
    $[6] = className;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== Comp || $[9] !== children || $[10] !== props || $[11] !== t1 || $[12] !== t2) {
    t3 = <Comp href={t1} className={t2} {...props}>{children}</Comp>;
    $[8] = Comp;
    $[9] = children;
    $[10] = props;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
export interface HeaderNavProps extends ComponentPropsWithoutChildren<"nav"> {
  children?: ReactNode;
}
export const HeaderNav: FC<HeaderNavProps> = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== children) {
    t1 = <ul className="flex items-center gap-3 sm:gap-8">{children}</ul>;
    $[4] = children;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== className || $[7] !== props || $[8] !== t1) {
    t2 = <nav className={className} {...props}>{t1}</nav>;
    $[6] = className;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export interface HeaderNavLinkProps extends ComponentPropsWithoutChildren<"a"> {
  children?: ReactNode;
  asChild?: boolean;
  active?: boolean;
}
export const HeaderNavLink: FC<HeaderNavLinkProps> = t0 => {
  const $ = _c(15);
  let active;
  let asChild;
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      asChild,
      active,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = active;
    $[2] = asChild;
    $[3] = children;
    $[4] = className;
    $[5] = props;
  } else {
    active = $[1];
    asChild = $[2];
    children = $[3];
    className = $[4];
    props = $[5];
  }
  const Comp = asChild ? Slot : "a";
  const t1 = active ? "opacity-100 underline decoration-[3px] underline-offset-4" : "opacity-70 hover:opacity-100";
  let t2;
  if ($[6] !== className || $[7] !== t1) {
    t2 = cn("text-xs font-bold uppercase tracking-[1px] text-[var(--fg-primary)] transition-opacity focus-visible:focus-ring-sm sm:text-sm", t1, className);
    $[6] = className;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = active ? "page" : undefined;
  let t4;
  if ($[9] !== Comp || $[10] !== children || $[11] !== props || $[12] !== t2 || $[13] !== t3) {
    t4 = <li className="list-none"><Comp className={t2} aria-current={t3} {...props}>{children}</Comp></li>;
    $[9] = Comp;
    $[10] = children;
    $[11] = props;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
export interface HeaderCtaProps extends ComponentPropsWithoutChildren<"a"> {
  children?: ReactNode;
  asChild?: boolean;
  active?: boolean;
}
export const HeaderCta: FC<HeaderCtaProps> = t0 => {
  const $ = _c(17);
  let active;
  let asChild;
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      asChild,
      active,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = active;
    $[2] = asChild;
    $[3] = children;
    $[4] = className;
    $[5] = props;
  } else {
    active = $[1];
    asChild = $[2];
    children = $[3];
    className = $[4];
    props = $[5];
  }
  const Comp = asChild ? Slot : "a";
  const t1 = active && "ring-2 ring-[var(--fg-primary)] ring-offset-2 ring-offset-[var(--bg-primary)]";
  let t2;
  if ($[6] !== className || $[7] !== t1) {
    t2 = cn("px-3 py-2 text-xs uppercase sm:px-6 sm:py-3 sm:text-sm", t1, className);
    $[6] = className;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = active ? "page" : undefined;
  let t4;
  if ($[9] !== Comp || $[10] !== children || $[11] !== props || $[12] !== t3) {
    t4 = <Comp aria-current={t3} {...props}>{children}</Comp>;
    $[9] = Comp;
    $[10] = children;
    $[11] = props;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t2 || $[15] !== t4) {
    t5 = <li className="list-none"><Button asChild={true} variant="highlight" className={t2}>{t4}</Button></li>;
    $[14] = t2;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
};